import React from 'react';
import { Link } from 'react-router-dom';
import { Instagram, Phone, Mail, MapPin, Heart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { label: 'Shop All', path: '/products' },
    { label: 'Customize', path: '/customize' },
    { label: 'About Us', path: '/about' },
    { label: 'Contact', path: '/about#contact' },
  ];

  const categories = [
    { label: 'Bridal Sarees', path: '/products/bridal-saree' },
    { label: 'Accessories', path: '/products/accessories' },
    { label: 'Gift Creations', path: '/products/gifts' },
    { label: 'New Arrivals', path: '/products/new-arrivals' },
  ];

  return (
    <footer className="bg-gradient-burgundy-soft border-t border-border/30 mt-auto">
      {/* Main Footer Content */}
      <div className="container mx-auto px-4 py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
          {/* Brand Column */}
          <div className="space-y-4">
            <Link to="/" className="inline-block">
              <span className="font-display text-2xl font-semibold text-silver-light">
                Fazila
              </span>
              <span className="font-display text-2xl font-light text-ivory ml-1">
                Creations
              </span>
            </Link>
            <p className="text-sm text-muted-foreground font-body leading-relaxed">
              Turning Nikah moments into timeless memories. Handcrafted with love, 
              care, and barakah for your most sacred celebrations.
            </p>
            <div className="flex gap-3 pt-2">
              <a
                href="https://instagram.com/fazila_creations"
                target="_blank"
                rel="noopener noreferrer"
                className="h-10 w-10 rounded-full border border-silver/30 flex items-center justify-center text-silver hover:bg-silver/10 hover:border-silver/50 transition-all duration-300"
                aria-label="Instagram"
              >
                <Instagram className="h-4 w-4" />
              </a>
              <a
                href="https://wa.me/919111841033"
                target="_blank"
                rel="noopener noreferrer"
                className="h-10 w-10 rounded-full border border-silver/30 flex items-center justify-center text-silver hover:bg-silver/10 hover:border-silver/50 transition-all duration-300"
                aria-label="WhatsApp"
              >
                <Phone className="h-4 w-4" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-display text-lg text-silver-light mb-4">Quick Links</h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-sm text-muted-foreground hover:text-silver transition-colors duration-300"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Categories */}
          <div>
            <h4 className="font-display text-lg text-silver-light mb-4">Categories</h4>
            <ul className="space-y-3">
              {categories.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-sm text-muted-foreground hover:text-silver transition-colors duration-300"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Newsletter */}
          <div>
            <h4 className="font-display text-lg text-silver-light mb-4">Stay Connected</h4>
            <p className="text-sm text-muted-foreground mb-4">
              Get updates on new collections and exclusive offers.
            </p>
            <form className="space-y-3" onSubmit={(e) => e.preventDefault()}>
              <Input
                type="email"
                placeholder="Enter your email"
                variant="luxury"
                className="h-11"
              />
              <Button variant="ivory" className="w-full">
                Subscribe
              </Button>
            </form>
          </div>
        </div>
      </div>

      {/* Contact Bar */}
      <div className="border-t border-border/30">
        <div className="container mx-auto px-4 py-4">
          <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-8 text-sm text-muted-foreground">
            <a
              href="tel:+919111841033"
              className="flex items-center gap-2 hover:text-silver transition-colors"
            >
              <Phone className="h-4 w-4" />
              <span>+91 9111841033</span>
            </a>
            <a
              href="mailto:hello@fazilacreations.com"
              className="flex items-center gap-2 hover:text-silver transition-colors"
            >
              <Mail className="h-4 w-4" />
              <span>hello@fazilacreations.com</span>
            </a>
            <span className="flex items-center gap-2">
              <MapPin className="h-4 w-4" />
              <span>India</span>
            </span>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-border/30 bg-primary-dark/50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-2 text-xs text-muted-foreground">
            <p>© {currentYear} Fazila Creations. All rights reserved.</p>
            <p className="flex items-center gap-1 font-body italic">
              Made with <Heart className="h-3 w-3 text-primary-light fill-primary-light" /> love, care, and barakah
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
